import { safeJsonParse } from "./index";
import { getPngImages } from "./svgUtils";

export const hexToRgb = (hex) => {
  let validHex = /^#?([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$/.test(hex);

  if (!validHex) {
    throw new Error("Invalid Hex Code");
  }

  hex = hex.replace(/^#/, "");

  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((char) => char + char)
      .join("");
  }

  let r = parseInt(hex.slice(0, 2), 16);
  let g = parseInt(hex.slice(2, 4), 16);
  let b = parseInt(hex.slice(4, 6), 16);

  return `${r}, ${g}, ${b}`;
};

function convertToKebabCase(input) {
  return input
    .replace(/([a-z])([A-Z])/g, "$1-$2") // Insert dash before capital letters
    .replace(/([a-zA-Z])(\d+)/g, "$1-$2") // Insert dash before numbers
    .toLowerCase();
}

export const changeColorScheme = (colorScheme) => {
  const root = document.documentElement;

  for (const [key, value] of Object.entries(colorScheme)) {
    const rgbColor = hexToRgb(value);

    root.style.setProperty(`--bs-${convertToKebabCase(key)}`, value);
    root.style.setProperty(`--bs-${convertToKebabCase(key)}-rgb`, rgbColor);
  }

  // Box Shadow
  root.style.setProperty("--bs-box-shadow", `0px 14px 56px 0px #E8EAF6`);
  root.style.setProperty("--bs-box-shadow_2", `0px 14px 36px 0px #DEE2F3`);
  root.style.setProperty("--bs-text-color", `${colorScheme?.textColor}`);
};

export const changeFontFamily = (fontConfig, preferredLanguage) => {
  const root = document.documentElement;

  const fontMapping = {
    EFFRA: {
      bold: "EFFRA-BOLD",
      black: "EFFRA-BOLD",
      heavy: "EFFRA-HEAVY",
      light: "EFFRA-LIGHT",
      medium: "EFFRA-MEDIUM",
      regular: "EFFRA-REGULAR",
      ultraLight: "EFFRA-LIGHT",
      italicBold: "EFFRA-BOLD-ITALIC",
      italicLight: "EFFRA-LIGHT-ITALIC",
      italicMedium: "EFFRA-MEDIUM-ITALIC",
      italicHeavy: "EFFRA-HEAVY-ITALIC",
    },
    "DIN-NEXT-LT-ARABIC": {
      bold: "DIN-NEXT-LT-ARABIC-BOLD",
      black: "DIN-NEXT-LT-ARABIC-BLACK",
      heavy: "DIN-NEXT-LT-ARABIC-HEAVY",
      light: "DIN-NEXT-LT-ARABIC-LIGHT",
      medium: "DIN-NEXT-LT-ARABIC-MEDIUM",
      regular: "DIN-NEXT-LT-ARABIC-REGULAR",
      ultraLight: "DIN-NEXT-LT-ARABIC-ULTRALIGHT",
    },
    "FOUNDERS-GROTESK": {
      bold: "FOUNDERS-GROTESK-BOLD",
      semibold: "FOUNDERS-GROTESK-SEMI-BOLD",
      medium: "FOUNDERS-GROTESK-MEDIUM",
      regular: "FOUNDERS-GROTESK-REGULAR",
      light: "FOUNDERS-GROTESK-LIGHT",
      italicBold: "FOUNDERS-GROTESK-BOLD-ITALIC",
      italicSemibold: "FOUNDERS-GROTESK-SEMI-BOLD-ITALIC",
      italicMedium: "FOUNDERS-GROTESK-MEDIUM-ITALIC",
      italicRegular: "FOUNDERS-GROTESK-REGULAR-ITALIC",
      italicLight: "FOUNDERS-GROTESK-LIGHT-ITALIC",
    },
    IBM_Plex_Sans_Arabic: {
      bold: "IBMPlexSansArabic-Bold",
      semibold: "IBMPlexSansArabic-SemiBold",
      medium: "IBMPlexSansArabic-Medium",
      regular: "IBMPlexSansArabic-Regular",
      light: "IBMPlexSansArabic-Light",
    },
  };

  const defaultFonts = {
    ar: "IBM_Plex_Sans_Arabic",
    en: "FOUNDERS-GROTESK",
  };

  const selectedFont =
    fontConfig?.font_config?.fontFamily?.[preferredLanguage] ||
    defaultFonts[preferredLanguage];
  if (!selectedFont || !fontMapping[selectedFont]) {
    console.warn(
      `Font not found or unsupported for "${preferredLanguage}". Falling back to default.`
    );
    return;
  }

  const styles = fontMapping[selectedFont];

  Object.entries(styles).forEach(([styleKey, fontValue]) => {
    root.style.setProperty(`--bs-font-family-${styleKey}`, fontValue);
  });
};

export const applyScreenStyle = (item) => {
  const containerId = item?.attributes?.containerId;
  const screenBgImage = item?.attributes?.screenBgImage;
  const screenElement = document.getElementById(containerId);
  if (!screenElement) {
    requestAnimationFrame(applyScreenStyle);
    return;
  }
  const screenStyle = safeJsonParse(item?.attributes?.screenStyle, {});
  Object.assign(screenElement.style, screenStyle);
  if (screenBgImage) {
    const imageUrl = getPngImages(screenBgImage);
    screenElement.style.backgroundImage = `url(${imageUrl})`;
    screenElement.style.backgroundSize = "cover";
    screenElement.style.backgroundRepeat = "no-repeat";
    screenElement.style.backgroundPosition = "center";
  }
};
